<template>
    <div class="p-2 md:p-0">
        <div class="flex items-center justify-start md:justify-between flex-wrap py-4 gap-x-2">
            <!--Main Page Header-->
            <div class="sm:flex-auto">
                <h1 class="text-xl font-semibold text-gray-900 select-none">
                    <span v-if="showPayPropUploadForm">PayProp File Imports</span>
                    <span v-else>{{ showArchivedAssetsList ? "Archived Assets" : "Assets" }}</span>
                </h1>
            </div>

            <div v-if="showMergeOption" tabindex="100" ref="mergeOptionDiv" @keydown="(event) => handleKeyAction('Escape', cancelAssetMerge, event)">
                <button class="select-none sm:ml-2 px-4 py-2 border border-transparent text-sm font-medium rounded-md shadow text-white bg-indigo-600 hover:bg-indigo-700"
                        @click.prevent="cancelAssetMerge">
                    Cancel Asset Merge
                </button>
            </div>

            <div class="flex items-center relative" v-if="!showMergeOption">
                <!--Back button to get out of PayProp File import view and back to assets list-->
                <button v-if="showPayPropUploadForm"
                        class="select-none sm:ml-2 px-4 py-2 border border-transparent text-sm font-medium rounded-md shadow text-white bg-indigo-600 hover:bg-indigo-700"
                        @click.prevent="showPayPropUploadForm = false">
                    Back
                </button>

                <template v-else>
                    <loading-indicator class="w-3 h-3 right-16 relative" v-if="exportLoading"></loading-indicator>
                    <!--Create New Asset - Primary Button. Only shows when no asset is selected-->
                    <button v-if="!showBulkActions && !showArchivedAssetsList && !exportLoading"
                            class="select-none sm:ml-2 px-4 py-2 border border-transparent text-sm font-medium rounded-md shadow text-white bg-indigo-600 hover:bg-indigo-700"
                            @click.prevent="showAssetForm">
                        New Asset
                    </button>

                    <button v-if="!showBulkActions && showArchivedAssetsList"
                            class="select-none sm:ml-2 px-4 py-2 border border-transparent text-sm font-medium rounded-md shadow text-white bg-indigo-600 hover:bg-indigo-700"
                            @click.prevent="toggleArchivedAssetsList">
                        Back
                    </button>

                    <!--Main Options Dropdown. Only shows when no asset is selected-->
                    <div v-if="!showBulkActions" class="ml-2">
                        <simple-dropdown :label="exportLoading ? 'Export In Progress...' : 'Options'">
                            <template slot="buttonOptions">
                                <simple-dropdown-option
                                    value="Show Archived Assets List"
                                    key="show_assets_list"
                                    @click="toggleArchivedAssetsList">
                                    Show {{ showArchivedAssetsList ? "Current" : "Archived" }} Assets
                                </simple-dropdown-option>
                                <simple-dropdown-option
                                    class="hidden sm:block"
                                    value="Merge Assets"
                                    key="show_merge_option"
                                    @click="showAssetMergeOption">
                                    {{ showMergeOption ? "Cancel Merge" : "Merge Assets" }}
                                </simple-dropdown-option>
                                <div class="border-b border-gray-200 my-2"></div>
                                <simple-dropdown-option
                                    value="Export Assets"
                                    key="export_assets"
                                    @click="exportAssets">
                                    Export Assets
                                </simple-dropdown-option>
                                <simple-dropdown-option
                                    v-if="app.state.user.support"
                                    value="Import Portfolio Data"
                                    key="import_portfolio_data"
                                    @click="showImportFileUploadForm">
                                    Import Portfolio Data
                                </simple-dropdown-option>
                                <template v-if="hasPaypropFilesIntegration">
                                    <div class="border-b border-gray-200 my-2"></div>
                                    <simple-dropdown-option
                                        value="Import PayProp Files"
                                        key="toggle_payprop_import_view"
                                        @click="showPayPropUploadForm = true">
                                        Import PayProp Files
                                    </simple-dropdown-option>
                                </template>
                            </template>
                        </simple-dropdown>
                    </div>

                    <!--Bulk Options Drop Down (Only shows if assets are selected)-->
                    <div v-else class="ml-2">
                        <simple-dropdown :label="bulkUpdateMenuLabel">
                            <template slot="buttonOptions">
                                <simple-dropdown-option
                                    value="Portfolio"
                                    key="update_portfolio"
                                    @click="showBulkUpdatePortfolioModal = true">
                                    Update Portfolio
                                </simple-dropdown-option>
                                <simple-dropdown-option
                                    value="Asset Group"
                                    key="update_asset_groups"
                                    @click="toggleAssetGroupsBulkUpdateModal">
                                    Update Asset Group
                                </simple-dropdown-option>
                                <div class="border-b border-gray-200 my-2"></div>
                                <simple-dropdown-option
                                    value="Primary Contact"
                                    key="set_primary_contact"
                                    @click="showSetPrimaryContactModal">
                                    Set Primary Contact
                                </simple-dropdown-option>
                                <div class="border-b border-gray-200 my-2"></div>
                                <simple-dropdown-option
                                    value="Archive / Unarchive"
                                    key="toggle_archive_modal"
                                    @click="showArchiveUnarchiveModal = true">
                                    {{ showArchivedAssetsList ? "Restore" : "Archive" }} Asset (s)
                                </simple-dropdown-option>
                            </template>
                        </simple-dropdown>
                    </div>
                </template>
            </div>
        </div>

        <template v-if="!showPayPropUploadForm">
            <expandable-filters-container
                :showExpandOption="true"
                :advancedFiltersExpanded="advancedSearch"
                filter-button-text="More Filters"
                @toggle-expand="toggleAdvancedSearch"
                @apply-filters="fetchAssets"
                @reset-filters="clearFilters">
                <!--Always Visible filters-->
                <div slot="top-panel-left">
                    <div class="flex justify-start items-center flex-wrap gap-x-2 gap-y-3">
                        <!--Mandate Type Filter-->
                        <simple-dropdown :label="currentMandateFilterLabel"
                                         v-model="mandateTypeFilter"
                                         @input="fetchAssets">
                            <template slot="buttonOptions">
                                <simple-dropdown-option
                                    v-for="mandateType in mandateTypes"
                                    :key="mandateType"
                                    :value="mandateType">
                                    {{ mandateType | titlecase }}
                                </simple-dropdown-option>
                            </template>
                        </simple-dropdown>

                        <!--Portfolio Filter-->
                        <multi-select-dropdown
                            ref="portfolio_search_dropdown"
                            v-model="selectedPortfolios"
                            search-type="remote"
                            uri="/settings/portfolios/all"
                            button-text="All Porfolios"
                            :hover-label-value="$options.filters.pluralizeOnlyS('Portfolio', selectedPortfolios.length)"
                            @input="doFilter">
                        </multi-select-dropdown>

                        <!--Asset and Tenant Text Filter-->
                        <simple-text-search-input
                            text-search-placeholder="eg. 100 Example Road"
                            text-search-label="Search Asset or Tenant"
                            input-styles="min-width:200px"
                            v-model="filterText"
                            @input="fetchAssets"
                        ></simple-text-search-input>

                        <span v-if="activeFiltersCount > 0"
                              class="h-6 md:ml-4 inline-flex items-center px-3 py-0.5 rounded-full text-sm font-medium bg-indigo-500 text-white">
                            <span>{{ activeFiltersCount + " Active Filter" | pluralizeOnlyS(activeFiltersCount) }}</span>

                            <button @click.prevent="clearFilters(); doFilter();" title="Clear All Filters"
                                    class="flex ml-2 cursor-pointer">
                                <icon-x-circle class="w-3 h-3 text-white hover:text-indigo-800 transition duration-300"></icon-x-circle>
                            </button>
                        </span>
                    </div>
                </div>

                <div slot="expanded-filters-container" class="bg-white flex justify-around p-8">
                    <div class="grid grid-cols-3 gap-4">
                        <!--Asset Group Filter-->
                        <div class="order-0 block col-span-4 md:col-span-2 md:order-1 lg:col-span-1 lg:order-0">
                            <label class="block font-medium">Asset Group</label>
                            <remote-select
                                v-model="assetGroupFilter"
                                @focus="searchAssetGroups"
                                :remote-method="searchAssetGroups"
                                :data="assetGroups"
                                :loading="loading"
                                item-label="name"
                                placeholder="Search Asset Groups">
                                <template slot-scope="props">
                                    {{ props.item.name }}
                                </template>
                            </remote-select>
                        </div>

                        <!--Reminder Range Filter-->
                        <div class="order-1 block col-span-4 md:col-span-2 md:order-2 lg:col-span-1 lg:order-1">
                            <label class="block font-medium">Next Reminder Range</label>
                            <el-date-picker
                                v-model="todoDateRangeFilter"
                                value-format="yyyy-MM-dd"
                                type="daterange"
                                range-separator="-"
                                start-placeholder="From date"
                                end-placeholder="To date">
                            </el-date-picker>
                        </div>

                        <!--Last Inspection Done Filter-->
                        <div class="order-3 block col-span-4 md:col-span-2 md:order-3 lg:col-span-1 lg:order-2">
                            <label class="block font-medium">Last Inspection</label>
                            <el-select v-model="lastInspectionDateFilter" class="w-100" clearable>
                                <el-option key="all" label="All" value="all"></el-option>
                                <el-option key="mt_3_months_ago" label="More Than 3 Months Ago" value="mt_3_months_ago"></el-option>
                                <el-option key="mt_6_months_ago" label="More Than 6 Months Ago" value="mt_6_months_ago"></el-option>
                                <el-option key="mt_12_months_ago" label="More Than 12 Months Ago" value="mt_12_months_ago"></el-option>
                                <el-option key="never" label="Never" value="never"></el-option>
                            </el-select>
                        </div>

                        <!--Next Scheduled Inspection Filter-->
                        <div class="order-5 block col-span-4 md:col-span-2 md:order-5 lg:col-span-1 lg:order-3">
                            <label class="block font-medium">Next Scheduled Inspection</label>
                            <el-select v-model="nextInspectionDateFilter" class="w-100" clearable>
                                <el-option key="all" label="All" value="all"></el-option>
                                <el-option key="lt_3_months" label="In Less Than 3 Months" value="lt_3_months"></el-option>
                                <el-option key="lt_6_months" label="In Less Than 6 Months" value="lt_6_months"></el-option>
                                <el-option key="lt_12_months" label="In Less Than 12 Months" value="lt_12_months"></el-option>
                                <el-option key="never" label="Never" value="never"></el-option>
                            </el-select>
                        </div>

                        <!--Inspection Type Existence Filter-->
                        <div v-if="inspectionTypes && inspectionTypes.length > 0" class="order-4 block col-span-4 md:col-span-2 md:order-4 lg:col-span-1 lg:order-4">
                            <label class="block font-medium">Has Inspection With Type</label>
                            <el-select v-model="inspectionTypeFilter" class="w-100" multiple>
                                <el-option
                                    v-for="type in inspectionTypes"
                                    :key="type"
                                    :label="type | capitalize"
                                    :value="type">
                                </el-option>
                            </el-select>
                        </div>

                        <!--Maintenance Ticket Filter-->
                        <div class="order-6 block col-span-4 md:col-span-2 md:order-6 lg:col-span-1 lg:order-5">
                            <label class="block font-medium">Last Ticket Opened</label>
                            <el-select v-model="lastTicketOpenedFilter" class="w-100" clearable>
                                <el-option key="tickets_all" label="All" value="all"></el-option>
                                <el-option key="tickets_mt_3_months" label="More Than 3 Months Ago" value="mt_3_months_ago"></el-option>
                                <el-option key="tickets_mt_6_months" label="More Than 6 Months Ago" value="mt_6_months_ago"></el-option>
                                <el-option key="tickets_mt_12_months" label="More Than 12 Months Ago" value="mt_12_months_ago"></el-option>
                                <el-option key="tickets_never" label="Never" value="never"></el-option>
                            </el-select>
                        </div>

                        <!--Maintenance Requests Filter-->
                        <div class="order-7 block col-span-4 md:col-span-2 md:order-7 lg:col-span-1 lg:order-6">
                            <label class="block font-medium">Last Maintenance Request Logged</label>
                            <el-select v-model="lastMaintenanceRequestFilter" class="w-100" clearable>
                                <el-option key="maint_request_all" label="All" value="all"></el-option>
                                <el-option key="maint_request_mt_3_months" label="More Than 3 Months Ago" value="mt_3_months_ago"></el-option>
                                <el-option key="maint_request_mt_6_months" label="More Than 6 Months Ago" value="mt_6_months_ago"></el-option>
                                <el-option key="maint_request_mt_12_months" label="More Than 12 Months Ago" value="mt_12_months_ago"></el-option>
                                <el-option key="maint_request_never" label="Never" value="never"></el-option>
                            </el-select>
                        </div>

                        <div class="order-7 block col-span-4 md:col-span-2 md:order-7 lg:col-span-1 lg:order-6">
                            <label class="block font-medium">Linked With Active Integration</label>
                            <el-select v-model="activeIntegrationFilter" class="w-100" clearable>
                                <el-option key="payprop" label="PayProp" value="payprop"></el-option>
                                <el-option key="rams" label="WeconnectU Rams" value="rams"></el-option>
                                <el-option key="replication" label="Replication From Other Team" value="replication"></el-option>
                            </el-select>
                        </div>

                    </div>
                </div>
            </expandable-filters-container>

            <!--Assets Table Section-->
            <div class="mt-4 overflow-hidden shadow sm:mx-0 rounded-lg">
                <!--Loading State-->
                <div v-if="assetListLoading || !mutableAssetsList">
                    <div class="flex justify-center items-center w-full p-20">
                        <div class="flex flex-col items-center">
                            <h2 class="text-xl font-normal text-gray-400 my-4 select-none flex align-items-center">
                                <icon-refresh class="h-8 w-8 text-indigo-600 animate-spin mr-2"/>
                                Loading...
                            </h2>
                        </div>
                    </div>
                </div>

                <!--Not Loading State-->
                <div v-else>
                    <!--Empty view if no matching assets are found-->
                    <div v-if="mutableAssetsList.length <= 0" class="flex justify-center items-center w-full p-20">
                        <div class="flex flex-col items-center">
                            <h2 class="text-xl font-normal text-gray-400 my-4 select-none">No matching assets found</h2>
                        </div>
                    </div>

                    <!--Display Asset Rows-->
                    <table v-else class="min-w-full divide-y divide-gray-300">
                        <thead class="bg-white">
                            <tr class="select-none">
                                <!--Bulk selection options-->
                                <th scope="col" class="px-6 py-3 text-left text-xs font-medium text-gray-400 align-center" style="max-width: 50px"
                                    v-if="!showMergeOption">
                                    <div class="inline-block">
                                        <div class="relative inline flex items-center justify-center -ml-2">
                                            <tailwind-simple-dropdown no-label>
                                                <template slot="buttonOptions" slot-scope="{hideDropdown}">
                                                    <div class="py-1" role="none">
                                                        <tailwind-simple-dropdown-option
                                                            @click="hideDropdown(); handlePageSelection('select-page')" value="">
                                                            Select current page
                                                        </tailwind-simple-dropdown-option>

                                                        <tailwind-simple-dropdown-option
                                                            @click="hideDropdown(); handlePageSelection('select-all')" value="">
                                                            Select all pages
                                                        </tailwind-simple-dropdown-option>

                                                        <tailwind-simple-dropdown-option
                                                            @click="hideDropdown(); handlePageSelection('un-select-all')" value="">
                                                            Un-select all
                                                            <span class="font-bold">{{ selectedRows.length }}</span>
                                                            selected rows
                                                        </tailwind-simple-dropdown-option>
                                                    </div>
                                                </template>
                                            </tailwind-simple-dropdown>
                                        </div>
                                    </div>
                                </th>

                                <th scope="col" class="py-3.5 pl-6 pr-3 text-left text-sm">
                                    <div class="inline-block ml-2">
                                        <div class="block md:inline-block text-gray-900 font-medium">Asset</div>
                                        <span class="hidden md:block text-indigo-600 font-normal">Portfolio</span>
                                        <span class="inline-block lg:hidden text-gray-900 whitespace-nowrap font-normal">Tenant</span>
                                        <span class="inline-block lg:hidden text-indigo-600 whitespace-nowrap font-normal"> / Landlord</span>
                                    </div>
                                </th>

                                <th scope="col" class="hidden px-3 py-3.5 text-left text-sm font-medium text-black lg:table-cell">
                                    <div class="flex items-start flex-col">
                                        <span class="block text-gray-900 whitespace-nowrap">Primary Landlord</span>
                                        <span class="block text-indigo-600 whitespace-nowrap">Primary Tenant</span>
                                    </div>
                                </th>

                                <th scope="col" class="hidden px-3 py-3.5 text-left text-sm font-medium text-green-600 lg:table-cell text-center">
                                    Latest Inspection
                                    <br>
                                    Uploaded
                                </th>

                                <th scope="col" class="hidden px-3 py-3.5 text-left text-sm font-medium text-indigo-600 lg:table-cell text-center">
                                    Next Scheduled
                                    <br>
                                    Inspection
                                </th>

                                <th scope="col"
                                    class="hidden px-3 py-3.5 text-left text-sm font-medium text-gray-900 text-center lg:table-cell lg:border-l lg:border-r border-gray-200">
                                    Next Reminder
                                </th>
                                <th class="hidden sm:table-cell" scope="col"></th>
                            </tr>
                        </thead>

                        <tbody class="divide-y divide-gray-200 bg-white">
                            <asset-row
                                v-for="asset in mutableAssetsList"
                                :key="asset.id"
                                :asset="asset"
                                :checked="isChecked(asset.id)"
                                @toggle-row-selection="toggleRowSelection"
                                @create-todo="toggleTodoModal"
                                @edit-todo="editTodo"
                                @merge-asset="showMergeModal"
                                @todo-clicked="toggleTodoModal"
                            ></asset-row>
                        </tbody>
                    </table>
                </div>
            </div>

            <simple-pagination
                v-if="paginationData && paginationData.total > 0"
                :meta="paginationData"
                per-page="10"
                :current-page="paginationData.current_page"
                padding="6"
                @page-changed="fetchAssets">
            </simple-pagination>
        </template>

        <!--Modals-->
        <div>
            <!--Portfolio Data Import Modal-->
            <simple-modal :show="showPortfolioDataImportModal" @close="showPortfolioDataImportModal = false" title="Upload Asset Import File">
                <div class="flex flex-col gap-4">
                    <el-upload
                        :action="assetImportUploadUrl"
                        :headers="assetImportUploadRequestHeaders"
                        :show-file-list="true"
                        :limit="1"
                        :on-success="handleImportUploadSuccess"
                        :on-error="handleImportUploadError">
                        <secondary-button type="primary">Select File</secondary-button>
                    </el-upload>

                    <div class="flex items-center justify-end">
                        <button
                            class="select-none sm:ml-2 px-4 py-2 border border-transparent text-sm font-medium rounded-md shadow text-white bg-indigo-600 hover:bg-indigo-700"
                            :disabled="importForm.busy"
                            @click.prevent="showPortfolioDataImportModal = false">Done
                        </button>

                        <button
                            class="select-none sm:ml-2 px-4 py-2 border border-gray-300 text-gray-700 shadow-sm hover:text-gray-500 text-sm font-medium rounded-md shadow text-white hover:text-gray-500 focus:outline-none focus:border-blue-300 focus:ring focus:ring-blue-400 transition ease-in-out duration-150 sm:text-sm sm:leading-5"
                            :disabled="importForm.busy" @click.prevent="showPortfolioDataImportModal = false">
                            Cancel
                        </button>
                    </div>
                </div>
            </simple-modal>

            <!--Create Asset Modal-->
            <create-asset></create-asset>

            <!--Property Merging Modal-->
            <merge-property-modal
                :asset="selectedAsset"
                @asset-merged="hideMergeModal(); doFilter();"
            ></merge-property-modal>

            <!--Bulk Update Portfolio Modal-->
            <confirmation-dialog
                :show="showBulkUpdatePortfolioModal"
                :show-icon="false"
                :title="`Move ${selectedRows.length} Selected Assets to Portfolio`"
                proceed-button-text="Move Assets"
                @proceed="moveSelectedAssetsToPortfolio"
                @cancelled="showBulkUpdatePortfolioModal = false">
                <div slot="body" class="mx-1 my-8">
                    <remote-select
                        v-model="selectedPortfolioForBulkUpdate"
                        :data="app.state.user.portfolios"
                        :loading="loading"
                        class="w-full"
                        item-label="name"
                        placeholder="Select Portfolio">
                        <template slot-scope="{item}">
                            {{ item.name }}
                        </template>
                    </remote-select>
                </div>
            </confirmation-dialog>

            <!--Bulk Archive or Unarchive Assets-->
            <confirmation-dialog
                :show="showArchiveUnarchiveModal"
                :show-icon="false"
                :title="archiveUnarchiveModalTitle"
                :proceed-button-text="showArchivedAssetsList ? 'Restore Assets' : 'Archive Assets'"
                @proceed="archiveOrRestoreSelectedAssets"
                @cancelled="showArchiveUnarchiveModal = false"
            ></confirmation-dialog>

            <!--Set Primary Contact Modal-->
            <simple-modal :show="setPrimaryContactModalVisible" @close="closeSetPrimaryContactModal"
                          :title="`Set Primary ${(bulkSetContactForm.role ? bulkSetContactForm.role : 'Contact')} For ${selectedRows.length} Assets`"
                          :disable-close="bulkUpdating">
                <div class="flex flex-col gap-2">
                    <div class="form-group mb-4">
                        <label class="font-medium text-gray-900">Select Role</label>
                        <el-select
                            v-model="bulkSetContactForm.role"
                            class="col-sm-12" style="padding:0"
                            placeholder="Select Role">
                            <el-option value="landlord" label="Landlord" key="landlord"></el-option>
                            <el-option value="tenant" label="Tenant" key="tenant"></el-option>
                            <el-option value="other" label="Other" key="other"></el-option>
                        </el-select>

                        <span
                            class="invalid-feedback"
                            :class="{'d-block': bulkSetContactForm.errors.has('category')}"
                            v-show="bulkSetContactForm.errors.has('category')">
                            {{ bulkSetContactForm.errors.get("category") }}
                        </span>
                    </div>

                    <div class="form-group mb-4">
                        <label for="searchContact" class="font-medium text-gray-900">Select Contact</label>
                        <div class="row" id="searchContact">
                            <div class="col-sm-12">
                                <el-select
                                    value-key="id"
                                    class="w-100"
                                    v-model="selectedContact"
                                    @focus="searchContacts('')"
                                    filterable
                                    remote
                                    :placeholder="'Search contact by Name / Email / Mobile'"
                                    :remote-method="searchContacts"
                                    :loading="loading">
                                    <el-option
                                        v-for="contact in suggestedContacts"
                                        :key="contact.id"
                                        :label="contact.full_name"
                                        :value="contact">
                                        <span class="position-relative">
                                            <span v-if="contact.pivot"
                                                  class="label label-light-primary mr-2">
                                                {{ contact.pivot.role }}
                                            </span>
                                            <span v-if="contact.juristic_name" class="mr-1">
                                                {{ contact.juristic_name }}
                                                <span class="text-info">({{ contact.full_name }})</span>
                                            </span>
                                            <span v-else>{{ contact.full_name }}</span>
                                        </span>
                                    </el-option>
                                </el-select>
                            </div>
                        </div>
                    </div>

                    <div class="form-group">
                        <checkbox v-model="bulkSetContactForm.remove_all_other">
                            <span class="mx-2 font-medium">Remove existing contacts in this role</span>
                        </checkbox>
                    </div>

                    <div class="flex items-center justify-end">
                        <button
                            class="select-none sm:ml-2 px-4 py-2 border border-transparent text-sm font-medium rounded-md shadow text-white bg-indigo-600 hover:bg-indigo-700"
                            :disabled="importForm.busy"
                            @click.prevent="setPrimaryContacts">{{ loading ? "Setting Contacts..." : "Confirm" }}
                        </button>

                        <button
                            class="select-none sm:ml-2 px-4 py-2 border border-gray-300 text-gray-700 shadow-sm hover:text-gray-500 text-sm font-medium rounded-md shadow text-white hover:text-gray-500 focus:outline-none focus:border-blue-300 focus:ring focus:ring-blue-400 transition ease-in-out duration-150 sm:text-sm sm:leading-5"
                            :disabled="importForm.busy" @click.prevent="closeSetPrimaryContactModal">
                            Cancel
                        </button>
                    </div>
                </div>
            </simple-modal>

            <!--Update Asset Groups Bulk Modal-->
            <confirmation-dialog
                :show="showChangeAssetsGroupsBulkUpdateModal"
                :show-icon="false"
                :title="`Move ${selectedRows.length} Selected Assets To New Asset Group`"
                proceed-button-text="Move Assets"
                @proceed="moveSelectedAssetsToAssetGroup"
                @cancelled="toggleAssetGroupsBulkUpdateModal()">
                <div class="flex flex-col items-end gap-4" slot="body">
                    <remote-select
                        v-model="selectedAssetGroupForBulkUpdate"
                        @focus="searchAssetGroups"
                        :remote-method="searchAssetGroups"
                        :data="assetGroups"
                        :loading="loading"
                        class="w-full"
                        item-label="name"
                        placeholder="Search Asset Groups">
                        <template slot-scope="props">
                            {{ props.item.name }}
                        </template>
                    </remote-select>
                </div>
            </confirmation-dialog>

            <!--Create Todo Modal-->
            <simple-modal :show="showTodoModal"
                          :title="this.selectedTodo ? 'Edit To-do' : 'Create To-do'"
                          @close="closeTodoModal">
                <todo-create-edit-form @close="closeTodoModal"
                                       @todo-created="handleTodoCreateOrUpdateEvent"
                                       @todo-updated="handleTodoCreateOrUpdateEvent"
                                       :todo="selectedTodo"
                                       :pre-selected-asset="assetForNewTodo"
                ></todo-create-edit-form>
            </simple-modal>
        </div>

        <!--Payprop File upload view-->
        <payprop-files-upload
            v-if="showPayPropUploadForm"
            :has-queued-imports="hasQueuedImports"
        ></payprop-files-upload>
    </div>
</template>

<script>
import AssetGroupSearch from "../../mixins/asset-group-search";
import GroupToggleSelect from "../library/GroupToggleSelect.vue";
import AssetRow from "./AssetRow.vue";
import TailwindSimpleDropdown from "../library/TailwindSimpleDropdown.vue";
import TailwindSimpleDropdownOption from "../library/TailwindSimpleDropdownOption.vue";
import { mapState } from "vuex";
import MultiSelectCheckboxHandlingMixin from "../../mixins/multi-select-checkbox-handling-mixin";

export default {
    name: "AssetsListNew",

    components: {TailwindSimpleDropdownOption, TailwindSimpleDropdown, AssetRow, GroupToggleSelect},

    mixins: [AssetGroupSearch, MultiSelectCheckboxHandlingMixin],

    data () {
        return {
            activeFiltersCount: 0,
            advancedSearch: false,
            assetForNewTodo: null,
            assetGroupFilter: null,
            assetImportUploadRequestHeaders: window.axios.defaults.headers.common,
            assetListLoading: true,
            bulkSetContactForm: new AppForm({}),
            bulkUpdating: false,
            filterText: "",
            hasPaypropFilesIntegration: false,
            importForm: new AppForm({}),
            inspectionFilters: false,
            inspectionTypeFilter: null,
            inspectionTypesFilter: null,
            lastInspectionDateFilter: null,
            lastMaintenanceRequestFilter: null,
            lastTicketOpenedFilter: null,
            loading: false,
            mandateTypeFilter: "All Mandates",
            mandateTypes: ["All Mandates", "unspecified", "managed", "unmanaged"],
            mutableAssetsList: [],
            nextInspectionDateFilter: null,
            paginationData: {},
            recommendationsFilter: null,
            reminderDateFilter: null,
            searchParams: {},
            selectedAsset: {},
            selectedAssetGroupForBulkUpdate: null,
            selectedContact: null,
            selectedPortfolioForBulkUpdate: null,
            selectedTodo: null,
            setPrimaryContactModalVisible: false,
            showArchivedAssetsList: false,
            showBulkActions: false,
            showBulkUpdatePortfolioModal: false,
            showChangeAssetsGroupsBulkUpdateModal: false,
            showArchiveUnarchiveModal: false,
            showPayPropUploadForm: false,
            showPortfolioDataImportModal: false,
            showTodoModal: false,
            suggestedContacts: [],
            todoDateRangeFilter: null,
            activeIntegrationFilter: null,
            inspectionTypes: [],
            hasQueuedImports: false,
            exportLoading: false,
            selectedPortfolios: []
        };
    },
    computed: {
        ...mapState({
            showMergeOption: state => state.assetList.showMergeOption,
        }),
        archiveUnarchiveModalTitle () {
            const countSelectedRows = this.selectedRows.length;
            return this.showArchivedAssetsList ? `Restore ${countSelectedRows} Selected Assets` : `Archive ${countSelectedRows} Selected Assets`;
        },
        selectedFiltersCount () {
            let count = 0;
            if (Object.keys(this.searchParams).length > 0) {
                if (this.searchParams.archived_filter) {count++;}
                if (this.searchParams.asset_group_filter) {count++;}
                if (this.searchParams.portfolio_ids_filter?.length > 0) {count++;}
                if (this.searchParams.mandate_type_filter && this.searchParams.mandate_type_filter !== "All Mandates") {count++;}
                if (this.searchParams.filter?.length > 0) {count++;}
                if (this.searchParams.last_inspection_date_filter && this.searchParams.last_inspection_date_filter !== "all") {count++;}
                if (this.searchParams.next_inspection_date_filter && this.searchParams.next_inspection_date_filter !== "all") {count++;}
                if (this.searchParams.last_ticket_date_filter && this.searchParams.last_ticket_date_filter !== "all") {count++;}
                if (this.searchParams.last_maintenance_request_date_filter && this.searchParams.last_maintenance_request_date_filter !== "all") {count++;}
                if (this.searchParams.inspection_type_filter && this.searchParams.inspection_type_filter.length > 0) {count++;}
                if (this.searchParams.todo_date_range_filter && this.searchParams.todo_date_range_filter.length !== 0) {count++;}
                if (this.searchParams.active_integration_filter && ["rams", "payprop", "replication"].includes(this.searchParams.active_integration_filter)) {count++;}
            }
            return count;
        },
        assetImportUploadUrl () {
            return "/assets/import";
        },
        currentMandateFilterLabel () {
            return this.searchParams.mandate_type_filter ?
                this.$options.filters.titlecase(this.mandateTypeFilter) :
                "All Mandates";
        },
        bulkUpdateMenuLabel () {
            return this.selectedRows.length > 1 ? "Bulk Update Assets" : "Update Asset";
        },
    },

    created () {
        this.selectAllUri = "assets/select/all";
        this.dataListPropertyName = "mutableAssetsList";

        this.searchAssetGroups();
        this.fetchInspectionTypes();
        this.getPaypropFilesIntegration();
        this.checkAssetsImportStatus();
        let self = this;
        Bus.$on("asset-created", function () {
            self.doFilter();
            self.hideAssetForm();
        });

        self.fetchAssets();
    },

    updated () {
        // We need this focus to be able to bind the event listener so the user can
        // press escape to cancel the merge option. Doing this in the updated hook
        // ensures we only focus after updates that insert the element.
        if (this.showMergeOption && this.$refs.mergeOptionDiv) {
            this.$refs.mergeOptionDiv.focus();
        }
    },

    watch: {
        selectedRows: {
            handler: function (val) {
                this.$set(this, "showBulkActions", val.length > 0);
            },
            deep: true,
            immediate: true
        }
    },

    methods: {
        fetchInspectionTypes () {
            axios.get("/inspections/lists/inspection-types")
            .then((response) => {
                this.inspectionTypes = response.data;
            })
            .catch((error) => {
                toastr.error("Could not fetch inspection types. " + error);
            });
        },
        checkAssetsImportStatus () {
            axios.get("/assets/import/check-status")
            .then((response) => {
                this.hasQueuedImports = response.data.hasQueuedImports;
            })
            .catch((error) => {
                toastr.error("Could not check portfolio import status. " + error);
            });
        },
        clearFilters () {
            this.showArchivedAssetsList = false;
            this.filterText = "";
            this.selectedPortfolios = [];
            this.mandateTypeFilter = "All Mandates";
            this.lastInspectionDateFilter = null;
            this.nextInspectionDateFilter = null;
            this.lastTicketOpenedFilter = null;
            this.assetGroupFilter = null;
            this.lastMaintenanceRequestFilter = null;
            this.inspectionTypeFilter = [];
            this.todoDateRangeFilter = [];
            this.activeIntegrationFilter = null;
            this.selectedPortfolios = [];
            this.$refs.portfolio_search_dropdown.clearSelection();
            this.doFilter();
        },
        handleKeyAction (key, callback, event) {
            if (event.key === key) {
                callback();
            }
        },
        showAssetMergeOption () {
            this.$store.commit("SET_SHOW_MERGE_OPTION_STATE", !this.showMergeOption);
        },
        cancelAssetMerge () {
            this.$store.commit("SET_SHOW_MERGE_OPTION_STATE", false);
        },
        selectMandateTypeFilter (mandateType) {
            this.$set(this.searchParams, "mandate_type_filter", mandateType);
            this.doFilter();
        },
        toggleAdvancedSearch () {
            this.advancedSearch = !this.advancedSearch;
        },
        initialiseBulkSetContactForm () {
            this.selectedContact = null;
            this.bulkSetContactForm = new AppForm({
                remove_all_other: false,
                selected_contact: null,
                role: "landlord",
                assets: this.selectedRows
            });
        },
        showSetPrimaryContactModal () {
            this.initialiseBulkSetContactForm();
            this.setPrimaryContactModalVisible = true;
        },
        closeSetPrimaryContactModal () {
            this.setPrimaryContactModalVisible = false;
        },
        setPrimaryContacts () {
            this.loading = true;
            this.bulkSetContactForm.selected_contact = this.selectedContact.id;

            axios.post("/assets/bulk/set-primary-contacts", this.bulkSetContactForm)
            .then(() => {
                this.closeSetPrimaryContactModal();
                toastr.success("Assets updated!");
                this.doFilter();
            }).catch((errors) => {
                toastr.error("Could not set primary " + this.bulkSetContactForm.role + " contacts. " + errors);
            }).finally(this.loading = false);
        },
        searchContacts (query) {
            axios.get("/contacts/search-to-link", {
                params: {
                    filter: query
                }
            }).then((response) => {
                this.suggestedContacts = response.data;
            }).catch((errors) => {
                toastr.error("Could not find any matching contacts!");
                this.loading = false;
            });
        },
        showAssetForm () {
            if (!this.verifyTeamIsSubscribed()) {
                return;
            }

            if (this.app.state.assets_remaining <= 0) {
                toastr.error("Property Limit Reached! Consider upgrading to Enterprise for unlimited assets!");
            } else {
                $("#modal-asset-form").modal("show");
            }
        },
        hideAssetForm () {
            $("#modal-asset-form").modal("hide");
        },
        showMergeModal (asset) {
            this.selectedAsset = asset;
            $("#merge-property-modal").modal("show");
        },
        hideMergeModal () {
            this.selectedAsset = null;
            $("#merge-property-modal").modal("hide");
        },

        doFilter: _.debounce(function () {
            this.assetListLoading = true;
            this.setSearchParams();
            this.fetchAssets();
        }, 500),
        fetchAssets: _.debounce(function (paginationSettings = null) {
            this.setSearchParams(paginationSettings);
            axios.get("/assets/all", {
                params: this.searchParams
            }).then((response) => {
                const {data, ...paginationData} = response.data;
                this.mutableAssetsList = data;
                this.paginationData = paginationData.meta;
                this.activeFiltersCount = this.selectedFiltersCount;
                this.assetListLoading = false;
            })
            .catch((errors) => {
                toastr.error("Could not fetch assets. " + errors);
                this.assetListLoading = false;
            });
        }, 500),
        archiveOrRestoreSelectedAssets () {
            const uri = this.showArchivedAssetsList ? "/assets/bulk/unarchive" : "/assets/bulk/archive";
            App.post(uri, new AppForm({
                assets: this.selectedRows
            })).then((message) => {
                toastr.success(message);
                this.selectedRows = [];
                this.doFilter();
            }).catch(error => {
                toastr.error(error.message);
            }).finally(() => {
                this.showArchiveUnarchiveModal = false;
            });
        },
        moveSelectedAssetsToPortfolio () {
            axios.post("/assets/bulk/portfolios/update", new AppForm({
                asset_ids: this.selectedRows,
                portfolio_id: this.selectedPortfolioForBulkUpdate.id
            })).then((response) => {
                toastr.success(response.data);
                this.showBulkUpdatePortfolioModal = false;
                this.doFilter();
            });
        },
        toggleAssetGroupsBulkUpdateModal () {
            this.showChangeAssetsGroupsBulkUpdateModal = !this.showChangeAssetsGroupsBulkUpdateModal;
        },
        moveSelectedAssetsToAssetGroup () {
            App.post("/assets/bulk/groups/update", new AppForm({
                assets: this.selectedRows,
                asset_group: this.selectedAssetGroupForBulkUpdate ? this.selectedAssetGroupForBulkUpdate.id : null
            })).then((response) => {
                toastr.success(response);
            }).catch((error) => {
                toastr.error(error.message);
            }).finally(() => {
                this.toggleAssetGroupsBulkUpdateModal();
                this.doFilter();
            });
        },
        toggleArchivedAssetsList () {
            this.assetListLoading = true;
            this.showArchivedAssetsList = !this.showArchivedAssetsList;
            this.doFilter();
        },
        showImportFileUploadForm () {
            this.showPortfolioDataImportModal = true;
        },
        handleImportUploadError (error, file, fileList) {
            toastr.error("File upload failed! Please try again.");
        },
        handleImportUploadSuccess (res, file) {
            toastr.success(res);
        },
        setSearchParams (paginationSettings = null) {
            this.searchParams = {
                "archived_filter": this.showArchivedAssetsList,
                "filter": this.filterText,
                "portfolio_ids_filter": this.selectedPortfolios && this.selectedPortfolios.length > 0 ? this.selectedPortfolios : [],
                "asset_group_filter": this.assetGroupFilter ? this.assetGroupFilter.id : null,
                "last_inspection_date_filter": this.lastInspectionDateFilter,
                "next_inspection_date_filter": this.nextInspectionDateFilter,
                "last_ticket_date_filter": this.lastTicketOpenedFilter,
                "last_maintenance_request_date_filter": this.lastMaintenanceRequestFilter,
                "inspection_type_filter": this.inspectionTypeFilter,
                "todo_date_range_filter": this.todoDateRangeFilter,
                "mandate_type_filter": this.mandateTypeFilter,
                "active_integration_filter": this.activeIntegrationFilter,
                page: paginationSettings && paginationSettings.pageNumber ? paginationSettings.pageNumber : 1,
                per_page: paginationSettings && paginationSettings.perPage ? paginationSettings.perPage : 10,
            };
        },
        exportAssets () {
            this.exportLoading = true;

            if (this.paginationData.total > 500) {
                toastr.info("You have a lot of assets. Please allow at least a minute for the export to complete.");
            }

            axios.get("/assets/export", {
                params: this.searchParams,
                responseType: "blob"
            }).then(response => {

                const url = window.URL.createObjectURL(new Blob([response.data], {type: response.data.type}));
                const link = document.createElement("a");

                link.href = url;
                link.setAttribute("download", moment().format("DD-MM-YYYY") + " Assets Export.xlsx");
                document.body.appendChild(link);
                link.click();

            }).catch(error => {
                toastr.error(error.data.message);
            }).finally(() => {
                this.exportLoading = false;
            });
        },
        getPaypropFilesIntegration () {
            axios.get("/integrations/payprop/check-status")
            .then(response => {
                this.hasPaypropFilesIntegration = response.data.hasIntegration;
            })
            .catch(error => {
                toastr.error(`Unable to fetch PayProp integration status. Error: ${error.message}`);
            });
        },
        toggleTodoModal (asset) {
            this.$set(this, "assetForNewTodo", asset);
            this.showTodoModal = !this.showTodoModal;
        },
        handleTodoCreateOrUpdateEvent () {
            this.closeTodoModal();
            this.doFilter();
        },
        closeTodoModal () {
            this.selectedTodo = null;
            this.showTodoModal = false;
        },
        editTodo (asset) {
            let self = this;
            axios.get("/todos/" + asset.next_todo.id)
            .then(function (response) {
                self.selectedTodo = response.data;
                self.showTodoModal = true;
            }).catch((errors) => {
                toastr.error("Could not fetch todo details. " + errors);
            });
        },
    },
};
</script>

